import React from 'react';

import { FullscreenLoader } from '@hrlogics/react-components';

import type { Nullable } from '@clearverify/types';

import {
  PeoStaffListDocument,
  PeoStaffRole,
  UserStatus,
  usePeoStaffMinimalListQuery,
  useSendInvitesToPeoStaffByPeoPayrollProviderStaffMutation,
} from 'src/api';
import { PEO_ROLE_OPTIONS } from 'src/constants/peo';
import { useNotification } from 'src/hooks/app';
import { usePeoStaffListQueryVariables } from 'src/hooks/peoStaff';

import { InvitePeoStaffForm } from './InvitePeoStaffForm';
import type { InviteFormFieldsValues } from './types';
import { convertStaff } from './utils/convertStaff';

interface InvitePeoStaffByPeoPayrollProviderModalFormProps {
  peoId: Nullable<string>;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InvitePeoStaffByPeoPayrollProviderModalForm: React.FC<
  InvitePeoStaffByPeoPayrollProviderModalFormProps
> = ({ peoId, closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const queryVariables = usePeoStaffListQueryVariables(peoId);

  const { data, loading } = usePeoStaffMinimalListQuery({
    variables: {
      filter: {
        peoId: { equals: peoId },
      },
    },
    skip: !peoId,
  });

  const [sendInvites] = useSendInvitesToPeoStaffByPeoPayrollProviderStaffMutation({
    refetchQueries: [{ query: PeoStaffListDocument, variables: queryVariables }],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const handleSubmit = React.useCallback(
    async (formFields: InviteFormFieldsValues) => {
      if (!peoId) return;

      switchClosability(false);

      try {
        await sendInvites({
          variables: {
            peoId,
            staff: convertStaff(formFields.staff),
          },
        });

        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [peoId, switchClosability, sendInvites, closeModal],
  );

  const roleFieldSelectOptions = React.useMemo(() => {
    const isEmptyExistingStaff = data?.peoStaffCount === 0;
    const isAllExistingStaffPending = data?.peoStaffList.every(
      staff => staff.user.status === UserStatus.Pending,
    );

    return PEO_ROLE_OPTIONS.filter(option => {
      if (isEmptyExistingStaff) {
        return option.value === PeoStaffRole.PeoSuperAdmin;
      }
      if (isAllExistingStaffPending) {
        return option.value !== PeoStaffRole.PeoManager;
      }
      return true;
    });
  }, [data?.peoStaffCount, data?.peoStaffList]);

  if (!peoId) {
    return <FullscreenLoader />;
  }

  return (
    <InvitePeoStaffForm
      peoId={peoId}
      roleFieldSelectOptions={roleFieldSelectOptions}
      onClose={closeModal}
      onSubmit={handleSubmit}
      loading={loading}
    />
  );
};
